import React from 'react';
import {FormProvider, useForm, useFormContext, useWatch} from 'react-hook-form';
import {useQueryClient} from '@tanstack/react-query';
import type {ProyectoFormDTO} from '../../../../api/Objetos';
import {informacionGeneralApi, proyectosPublicosApi} from '../../../../api/api';
import {createEmptyProyecto} from '../emptyProyecto';
import {useCrudSection} from '../useCrudSection';
import {useRegisterSectionSave} from '../SectionSaveCoordinator';
import {
    DateField,
    FieldGrid,
    NumberField,
    SelectField,
    TextAreaField,
    TextField,
    CurrencyField,
    ToggleField
} from '../../../../ui/form/fields';
import {SectionAlerts, SectionSaveButton} from '../sectionCrudUtils';
import {FormSection, StatusPill, getStatusTone, hasPersistedId, useProjectReadOnly, useProjectPublicView} from '../projectForm.helpers';
import BarraCargando from "../../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import {DocumentosAdjuntosPanel} from '../DocumentosAdjuntosPanel';

const getEstadoOptions = (t: (key: string) => string) => [
    {value: 'En Ejecucion', label: t('informacionGeneralSection.statusOptions.En Ejecucion')},
    {value: 'Solicitado', label: t('informacionGeneralSection.statusOptions.Solicitado')},
    {value: 'En Valoracion', label: t('informacionGeneralSection.statusOptions.En Valoracion')},
    {value: 'Concedido', label: t('informacionGeneralSection.statusOptions.Concedido')},
    {value: 'Justificacion', label: t('informacionGeneralSection.statusOptions.Justificacion')},
    {value: 'Denegada', label: t('informacionGeneralSection.statusOptions.Denegada')},
    {value: 'Proceso auditoria', label: t('informacionGeneralSection.statusOptions.Proceso auditoria')},
    {value: 'Ejecutada', label: t('informacionGeneralSection.statusOptions.Ejecutada')},
    {value: 'Borrador', label: t('informacionGeneralSection.statusOptions.Borrador')}
];

const sanitizarEstado = (val: string | undefined, t: (key: string) => string): string => {
    if (!val) return '';
    const normal = val.trim().toLowerCase()
        .replace('ó', 'o').replace('á', 'a').replace('í', 'i').replace('é', 'e');

    const options = getEstadoOptions(t);
    const coincidencia = options.find(opt => opt.value.toLowerCase() === normal);
    return coincidencia ? coincidencia.value : val;
};

export function InformacionGeneralSection(props: { proyectoId: number; showIds?: boolean }) {
    const publicView = useProjectPublicView();
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const form = useForm<ProyectoFormDTO>({
        defaultValues: createEmptyProyecto(),
    });
    const queryClient = useQueryClient();

    const section = useCrudSection({
        queryKey: [publicView ? 'public' : 'admin', 'proyectos', 'informacion-general'],
        proyectoId: props.proyectoId,
        getFn: publicView
            ? async proyectoId => (await proyectosPublicosApi.get(proyectoId)).informacionGeneral ?? null
            : informacionGeneralApi.get,
        saveFn: informacionGeneralApi.save,
        onSuccess: async (savedData) => {
            queryClient.setQueryData(['admin', 'proyectos', 'meta', props.proyectoId], (current: any) => {
                if (!current) return current;

                return {
                    ...current,
                    informacionGeneral: {
                        ...(current.informacionGeneral ?? {}),
                        ...savedData,
                    },
                };
            });
        },
    });
    React.useEffect(() => {
        if (section.data) {
            const infoSanitizada = {
                ...section.data,
                estado: sanitizarEstado(section.data.estado, t)
            };

            form.reset({
                ...createEmptyProyecto(),
                informacionGeneral: infoSanitizada,
            });
        }
    }, [form, section.data, t]);

    const handleSave = form.handleSubmit(async (value) => {
        await section.save(value.informacionGeneral ?? {});
    });

    useRegisterSectionSave({
        id: 'informacion-general',
        isDirty: form.formState.isDirty,
        isSaving: section.saveMutation.isPending,
        save: async () => {
            await handleSave();
        },
    });

    return (
        <FormProvider {...form}>
            <InformacionGeneralSectionFields
                proyectoId={props.proyectoId}
                showIds={Boolean(props.showIds)}
                isLoading={section.isLoading}
                loadError={section.error}
            />
            {!readOnly && (
                <>
                    <SectionSaveButton onClick={() => void handleSave()} busy={section.saveMutation.isPending}/>
                </>
            )}
        </FormProvider>
    );
}

function InformacionGeneralSectionFields(props: { proyectoId: number; showIds: boolean, isLoading: boolean, loadError: any }) {
    const { t } = useTranslation();
    const form = useFormContext<ProyectoFormDTO>();
    const showId = (path: string) => props.showIds && hasPersistedId(form.watch(path as never));
    const estado = useWatch({
        control: form.control,
        name: 'informacionGeneral.estado',
    });
    const readOnly = useProjectReadOnly();
    const publicView = useProjectPublicView();
    const ESTADO_OPTIONS = getEstadoOptions(t);

    return (
        <FormSection
            id="informacion-general"
            title={t('informacionGeneralSection.title')}
            collapsible
            defaultOpen={false}
            headerActions={!readOnly &&
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                    {props.isLoading && <BarraCargando size="1.2em" />}
                    <ToggleField
                        label={t('informacionGeneralSection.public')}
                        name="informacionGeneral.publico"
                        register={form.register}
                    />
                </div>
            }
        >
            <SectionAlerts loadError={props.loadError}/>
            <div className="sectionLead">
                <div className="statusPillRow">
                    <StatusPill label={t('informacionGeneralSection.statusLabel', { status: estado || t('informacionGeneralSection.statusUndefined') })} tone={getStatusTone(estado)}/>
                </div>
            </div>
            <FieldGrid>
                {showId('informacionGeneral.id') ?
                    <NumberField label={t('informacionGeneralSection.projectId')} name="informacionGeneral.id" register={form.register}/> : null}
                <TextField label={t('informacionGeneralSection.denomination')} name="informacionGeneral.denominacion"
                           register={form.register}/>
                <SelectField label={t('informacionGeneralSection.status')} name="informacionGeneral.estado" register={form.register}
                             options={ESTADO_OPTIONS}/>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.ministryReference')}
                               name="informacionGeneral.numeroExpedienteMinisterio"
                               register={form.register}/>
                </PrivateField>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.cityHallReference')}
                               name="informacionGeneral.numeroExpedienteAyuntamiento"
                               register={form.register}/>
                </PrivateField>
                <TextField label={t('informacionGeneralSection.ministryDepartment')}
                           name="informacionGeneral.ministerioConselleria" register={form.register}/>
                <TextField label={t('informacionGeneralSection.callAndOrder')} name="informacionGeneral.ordenYConvocatoria"
                           register={form.register}/>
                <LinkField
                    label={t('informacionGeneralSection.platformUrl')}
                    name="informacionGeneral.urlPlataforma"
                    register={form.register}
                />
                <PrivateField>
                    <LinkField label={t('informacionGeneralSection.coffeUrl')} name="informacionGeneral.urlCoffe" register={form.register}/>
                </PrivateField>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.contactPerson')} name="informacionGeneral.personaContacto" register={form.register}/>
                </PrivateField>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.department')} name="informacionGeneral.consejeria" register={form.register}/>
                </PrivateField>
                <PrivateField>
                    <NumberField label={t('informacionGeneralSection.grantScore')} name="informacionGeneral.puntuacionConcesionSubvencion" register={form.register} step={1}/>
                </PrivateField>

                <CurrencyField
                    label={t('informacionGeneralSection.totalAmount')}
                    name="informacionGeneral.importeTotal"
                    register={form.register}
                />
                <CurrencyField
                    label={t('informacionGeneralSection.subsidizedAmount')}
                    name="informacionGeneral.importeSubvencionado"
                    register={form.register}
                />

                <PrivateField>
                    <TextField label={t('informacionGeneralSection.bankExtract')} name="informacionGeneral.extractoBanco" register={form.register}/>
                </PrivateField>

                <PrivateField>
                    <CurrencyField label={t('informacionGeneralSection.municipalContribution')} name="informacionGeneral.aportacionMunicipal" register={form.register}/>
                </PrivateField>

                <DateField
                    label={t('informacionGeneralSection.requestDate')}
                    name="informacionGeneral.fechaSolicitud"
                    register={form.register}/>

                <DateField
                    label={t('informacionGeneralSection.grantDate')}
                    name="informacionGeneral.fechaConcesionResolucion"
                    register={form.register}
                />
                <TextField label={t('informacionGeneralSection.finalResolution')}
                           name="informacionGeneral.resolucionDefinitiva" register={form.register}/>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.linkedRecords')} name="informacionGeneral.expedientesVinculados" register={form.register}/>
                </PrivateField>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.leverPolicy')} name="informacionGeneral.politicaPalanca" register={form.register}/>
                </PrivateField>
                <TextField label={t('informacionGeneralSection.prtrComponent')} name="informacionGeneral.componentePRTR"
                           register={form.register}/>
                <PrivateField>
                    <TextField label={t('informacionGeneralSection.investment')} name="informacionGeneral.inversion" register={form.register}/>
                </PrivateField>
            </FieldGrid>

            <PrivateField>
                <TextAreaField label={t('informacionGeneralSection.observations')} name="informacionGeneral.observaciones" register={form.register} rows={4}/>
            </PrivateField>
            {!publicView ? (
                <div className="projectMediaAdmin">
                    <div className="adminNestedPanel">
                        <h3>Cabecera de logotipos</h3>
                        <p className="muted">Logotipos institucionales y de financiación que se mostrarán en la ficha pública.</p>
                        <DocumentosAdjuntosPanel proyectoId={props.proyectoId} ambito="LOGO_PROYECTO"
                            accept="image/png,image/jpeg,image/webp" imagePreview compact/>
                    </div>
                    <div className="adminNestedPanel">
                        <h3>Galería de imágenes</h3>
                        <p className="muted">Hasta 10 imágenes por proyecto.</p>
                        <DocumentosAdjuntosPanel proyectoId={props.proyectoId} ambito="GALERIA_PROYECTO"
                            accept="image/png,image/jpeg,image/webp" maxFiles={10} imagePreview compact/>
                    </div>
                </div>
            ) : null}
        </FormSection>
    );
}

function PrivateField(props: { children: React.ReactNode }) {
    const publicView = useProjectPublicView();
    return publicView ? null : <>{props.children}</>;
}

function LinkField(props: { label: string; name: string; register: any }) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    const { control } = useFormContext();
    const value = useWatch({ control, name: props.name });
    const getHref = (url: string) => {
        if (!url) return '';
        return url.startsWith('http') ? url : `https://${url}`;
    };

    if (readOnly) {
        return (
            <div className="label">
                {props.label}
                <div className="input" style={{ display: 'flex', alignItems: 'center', minHeight: '52px' }}>
                    {value ? (
                        <a
                            href={getHref(value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <span>{t('informacionGeneralSection.goToPlatform')}</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                        </a>
                    ) : (
                        <span className="muted">{t('informacionGeneralSection.notConfigured')}</span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <label className="label">
            {props.label}
            <div style={{ display: 'flex', gap: '8px' }}>
                <input
                    className="input"
                    placeholder="https://..."
                    {...props.register(props.name)}
                    style={{ flex: 1 }}
                />
                {value && (
                    <a
                        href={getHref(value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btnSecondary"
                        title={t('informacionGeneralSection.testLink')}
                        style={{ padding: '0 12px', display: 'flex', alignItems: 'center' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            <polyline points="15 3 21 3 21 9"></polyline>
                            <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                    </a>
                )}
            </div>
        </label>
    );
}
